import { FieldPath } from 'react-hook-form'
import { z } from 'zod'

import { usePostApiV1AccountsSignupMutation } from '../../../../authApi'
import { useEffect } from 'react'
import { getErrorMsg } from '@wellesley/error-kit'
import { fixedCacheKey } from './useSubmit'
import {
  displayNameValidator,
  emailValidator,
  passwordValidator,
  usernameValidator,
  useCommonForm,
  useUniqueUsername,
  getUsernameConflictError,
} from '@wellesley/forms'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

const formSchema = z.object({
  userName: usernameValidator({ showInlineError: true }),
  displayName: displayNameValidator,
  email: emailValidator,
  password: passwordValidator,
})

export type RegisterInput = z.infer<typeof formSchema>

export const useForm = () => {
  const [, { error }] = usePostApiV1AccountsSignupMutation({ fixedCacheKey })

  const form = useCommonForm({
    schema: formSchema,
    defaultValues: {
      userName: '',
      email: '',
      displayName: '',
      password: '',
    },
    showRequiredErrors: false,
  })

  const { isUserNameChecked, handleUserNameChange, handleUserNameBlur } =
    useUniqueUsername({ form })

  useEffect(() => {
    if (error) {
      const errMsg = getErrorMsg(error)!
      const setError = (fieldName: FieldPath<RegisterInput>, error = errMsg) =>
        form.setError(
          fieldName,
          { type: 'onChange', message: error },
          { shouldFocus: true },
        )

      const isEmailInUse =
        /email/i.test(errMsg) && (error as FetchBaseQueryError).status === 409

      if (isEmailInUse) {
        setError('email', 'Email is in use. Please try another')
        return
      }

      const usernameConflictError = getUsernameConflictError(error)

      if (usernameConflictError.isError) {
        setError('userName', usernameConflictError.message)
        return
      }

      switch (true) {
        case /email/i.test(errMsg): {
          setError('email')
          break
        }
        case /username/i.test(errMsg): {
          setError('userName')

          break
        }
      }
    }
  }, [error, form.setError])

  return { form, isUserNameChecked, handleUserNameChange, handleUserNameBlur }
}
